import { defineAsyncComponent } from 'vue';

import { COMMUNITY_ROUTE_NAMES } from '@/app/community/constants';
import LDRouteGuard from '@/components/core/LDRouteGuard';
import { communityRouteName } from '@/models/community/routes.enum';
import { useIdentityStore } from '@/stores/identity';

const Overview = () => import('@/app/community/components/overview/Overview.vue');
const TopPosts = () => import('@/app/community/components/overview/TopPosts.vue');
const App = () => import('@/app/community/App.vue');

const Interactions = () => import('@/app/community/pages/Interactions.vue');
const InternalNotes = () => import('@/app/community/pages/InternalNotes.vue');
const TeamActivity = defineAsyncComponent(() => import('@/app/community/pages/TeamActivity.vue'));

const PAGE_LEVEL_TOP = 'Community';

function redirectToAllMessages(to, from, next) {
  const identityStore = useIdentityStore();
  next({
    name: COMMUNITY_ROUTE_NAMES.ALL_MESSAGES,
    params: { brandLabel: identityStore.currentBrand?.label },
  });
}

export const getCommentPopupChildren = (parentRoute) => [
  {
    name: `${parentRoute}.linkedin_comments`,
    path: 'linkedin-comments/:interactionId',
    meta: {
      analytics: `${PAGE_LEVEL_TOP} - LinkedIn comment`,
      modal: { component: 'LinkedinCommentPopup' },
    },
  },
  {
    name: `${parentRoute}.instagram_comments`,
    path: 'instagram-comments/:interactionId',
    meta: {
      analytics: `${PAGE_LEVEL_TOP} - Instagram comment`,
      modal: {
        component: 'InstagramCommentPopup',
      },
    },
  },
  {
    name: `${parentRoute}.tiktok_comments`,
    path: 'tiktok-comments/:interactionId',
    meta: {
      analytics: `${PAGE_LEVEL_TOP} - TikTok comment`,
      modal: {
        component: 'TiktokCommentPopup',
      },
    },
  },
  {
    name: `${parentRoute}.facebook_comments`,
    path: 'facebook-comments/:interactionId',
    meta: {
      analytics: `${PAGE_LEVEL_TOP} - Facebook comment`,
      modal: {
        component: 'FacebookCommentPopup',
      },
    },
  },
  {
    path: 'tweets:pathMatch(.*)*',
    redirect: (to) => ({ ...to, path: to.path.replace('/tweets', '/posts') }),
  },
  {
    name: `${parentRoute}.tweets`,
    path: 'posts/:interactionId',
    meta: {
      analytics: `${PAGE_LEVEL_TOP} - Tweets`,
      modal: {
        component: 'TwitterRepliesPopup',
      },
    },
  },
  {
    name: `${parentRoute}.youtube_comments`,
    path: 'youtube-comments/:interactionId',
    meta: {
      analytics: `${PAGE_LEVEL_TOP} - YouTube comment`,
      modal: {
        component: 'YouTubeCommentPopup',
      },
    },
  },
];

export default {
  path: 'community',
  name: communityRouteName,
  component: App,
  meta: {
    requiresAuth: true,
    analytics: PAGE_LEVEL_TOP,
  },
  children: [
    // Route meta flags:
    // - `reportRoute`: report-style pages that share the same UI layout (report header, no sort / multi-select).
    //    Covers Insights, Top Posts, and Team Activity.
    // - `overviewRoute`: Insights analytics pages specifically (Insights, Top Posts). Drives
    //    Insights-only data rules, e.g. excluding interaction types Insights doesn't support.
    {
      path: 'insights',
      children: [
        {
          name: COMMUNITY_ROUTE_NAMES.INSIGHTS,
          path: '',
          component: Overview,
          meta: {
            title: 'Insights',
            analytics: `${PAGE_LEVEL_TOP} - Insights`,
            overviewRoute: true,
            reportRoute: true,
          },
        },
        {
          name: COMMUNITY_ROUTE_NAMES.TOP_POSTS,
          path: 'top-posts',
          component: TopPosts,
          meta: {
            analytics: `${PAGE_LEVEL_TOP} - Top Posts`,
            overviewRoute: true,
            reportRoute: true,
          },
        },
      ],
    },
    {
      name: COMMUNITY_ROUTE_NAMES.TEAM_ACTIVITY,
      path: 'team-activity',
      component: LDRouteGuard,
      props: {
        component: TeamActivity,
        requiredFeatureFlag: 'communityTeamActivity',
        to: () => ({
          name: COMMUNITY_ROUTE_NAMES.ALL_MESSAGES,
          params: { brandLabel: useIdentityStore().currentBrand?.label },
        }),
      },
      meta: {
        title: 'Team Activity',
        analytics: `${PAGE_LEVEL_TOP} - Team Activity`,
        reportRoute: true,
      },
    },
    {
      name: COMMUNITY_ROUTE_NAMES.INBOX,
      path: 'inbox',
      component: Interactions,
      beforeEnter: redirectToAllMessages,
      meta: {
        title: 'Inbox',
        analytics: `${PAGE_LEVEL_TOP} - Inbox`,
      },
      children: getCommentPopupChildren(COMMUNITY_ROUTE_NAMES.INBOX),
    },
    {
      name: COMMUNITY_ROUTE_NAMES.ASSIGNED_TO_YOU,
      path: 'assigned-to-you',
      component: Interactions,
      meta: {
        title: 'Assigned to You',
        analytics: `${PAGE_LEVEL_TOP} - Assigned to You`,
      },
      children: getCommentPopupChildren(COMMUNITY_ROUTE_NAMES.ASSIGNED_TO_YOU),
    },
    {
      name: COMMUNITY_ROUTE_NAMES.ARCHIVE,
      path: 'archive',
      component: Interactions,
      beforeEnter: redirectToAllMessages,
      meta: {
        title: 'Closed',
        analytics: `${PAGE_LEVEL_TOP} - Closed`,
      },
      children: getCommentPopupChildren(COMMUNITY_ROUTE_NAMES.ARCHIVE),
    },
    {
      name: COMMUNITY_ROUTE_NAMES.ALL_MESSAGES,
      path: 'all-messages',
      component: Interactions,
      meta: {
        title: 'All Messages',
        analytics: `${PAGE_LEVEL_TOP} - All Messages`,
      },
      children: getCommentPopupChildren(COMMUNITY_ROUTE_NAMES.ALL_MESSAGES),
    },
    {
      name: COMMUNITY_ROUTE_NAMES.INTERNAL_NOTES,
      path: 'internal-notes',
      component: InternalNotes,
      meta: {
        title: 'Internal Notes',
        analytics: `${PAGE_LEVEL_TOP} - Internal Notes`,
        collaborationRoute: true,
      },
      children: getCommentPopupChildren(COMMUNITY_ROUTE_NAMES.INTERNAL_NOTES),
    },
    {
      name: COMMUNITY_ROUTE_NAMES.CUSTOM_VIEWS,
      path: 'custom-views/:viewId',
      component: Interactions,
      meta: {
        title: 'Custom Views',
        analytics: `${PAGE_LEVEL_TOP} - Custom Views`,
        customViewRoute: true,
      },
      children: getCommentPopupChildren(COMMUNITY_ROUTE_NAMES.CUSTOM_VIEWS),
    },
    ...getCommentPopupChildren('community'),
  ],
};
